import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

const PDFPreviewDialog = ({ open, onClose, fileId, tenantId }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    const fetchPDFPreview = async () => {
      if (!open || !fileId || !tenantId) return;

      setLoading(true);
      setError(null);
      setPdfUrl(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        // Step 1: Get presigned URL
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileId}/presigned-url`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to get presigned URL: ${response.status}`);
        }

        const presignedData = await response.json();

        if (!presignedData.url) {
          throw new Error('No presigned URL received');
        }

        // Step 2: Fetch bytes and preview via a blob URL typed as application/pdf.
        // Avoids blank iframe + browser download when S3 returns octet-stream
        // (e.g. keys/filenames ending in ".PDF").
        const fileResponse = await fetch(presignedData.url);
        if (!fileResponse.ok) {
          throw new Error(`Failed to fetch PDF: ${fileResponse.status}`);
        }

        const sourceBlob = await fileResponse.blob();
        const pdfBlob =
          sourceBlob.type === 'application/pdf'
            ? sourceBlob
            : new Blob([sourceBlob], { type: 'application/pdf' });

        objectUrl = URL.createObjectURL(pdfBlob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPdfUrl(objectUrl);
      } catch (err) {
        console.error('Error fetching PDF preview:', err);
        if (!cancelled) {
          setError(err.message || 'Failed to load PDF preview');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPDFPreview();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, fileId, tenantId]);

  const handleClose = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          PDF Preview
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="400px"
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && pdfUrl && (
          <Box sx={{ height: '100%', width: '100%' }}>
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="PDF Preview"
            />
          </Box>
        )}

        {!loading && !error && !pdfUrl && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No PDF preview available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PDFPreviewDialog;
